import {useEffect, useRef, useState} from 'react';
import styled from '@emotion/styled';
import {MaskInput} from 'maska';

import theme from 'theme';
import {useRefMerge} from 'common';
import {Tooltip} from 'Tooltip';

import type {CSSProperties} from 'react';
import type {InputInternalProps, InputStyles} from '../types';

export const inputStyle = (style: InputStyles, acc: CSSProperties | undefined) => {
  const {isSmall, isFocused, isFilled, isDisabled, isLabeled, isReadOnly} = style;

  return {
    minHeight: isSmall ? 32 : 48,
    paddingTop: isFilled && isLabeled && !isSmall ? 14 : 0,
    textOverflow: !isFocused ? 'ellipsis' : undefined,
    cursor: isReadOnly ? 'default' : undefined,
    color: theme.colors.grey[isDisabled ? 40 : 100],

    ...acc,
  };
};

export const FieldComponent = styled.input`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;
  outline: none;
  border: none;
  background-color: transparent;
  vertical-align: top;
  caret-color: ${theme.colors.accent[90]};

  ${theme.typography.p1}

  ::selection {
    background-color: ${theme.colors.accent[10]};
  }

  [type='number'] {
    -webkit-appearance: none;
    -moz-appearance: textfield;
    appearance: textfield;

    ::-webkit-inner-spin-button {
      -webkit-appearance: none;
    }
  }

  :is(:-webkit-autofill, :autofill) {
    -webkit-box-shadow: inset 0 0 0 100px var(--input-bg-color, #fff);
    -webkit-text-fill-color: ${theme.colors.grey[100]};

    ~ [data-label] {
      -webkit-box-shadow: inset 0 0 0 100px var(--input-bg-color, #fff);
      -webkit-text-fill-color: ${theme.colors.grey[100]};
      transition: none;
    }
  }
`;

const placeholderStyle = ({isSmall, isFilled, isFocused, isLabeled}: InputStyles) => ({
  display: isFocused || isFilled ? 'flex' : 'none',
  paddingTop: isLabeled && isFilled && !isSmall ? 8 : 0,
  height: isSmall ? 32 : 48,
  lineHeight: isSmall ? '32px' : '48px',
});

const Placeholder = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  pointer-events: none;
  user-select: none;
  background-color: transparent;
  white-space: pre-wrap;
  color: ${theme.colors.grey[60]};
  box-sizing: border-box;

  ${theme.typography.p1}

  div:first-of-type {
    visibility: hidden;
  }
`;

const MaskedField: React.FC<InputInternalProps> = props => {
  const {mask, value, style, inputProps, qa} = props;

  const ref = useRefMerge<HTMLInputElement>(inputProps?.ref);
  const maskaRef = useRef<MaskInput>();

  useEffect(() => {
    if (!mask?.key || !maskaRef.current) {
      return;
    }

    maskaRef.current.update(mask);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mask?.key]);

  useEffect(() => {
    if (!ref.current || maskaRef.current?.items.has(ref.current)) {
      return;
    }

    if (maskaRef.current) {
      maskaRef.current.updateValue(ref.current);
    } else {
      maskaRef.current = new MaskInput(ref.current, {eager: true, ...mask});
    }

    /** Проблема с отсутствием в items */
    // return () => { maskaRef.current?.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const placeHolder = typeof mask?.mask === 'string' && (
    <Placeholder style={placeholderStyle(style)}>
      <div>{value}</div>
      <div>{mask.mask.substring(value?.length ?? 0)}</div>
    </Placeholder>
  );

  const componentProps = {
    ...inputProps,

    type: props.type,
    onInput: props.onChange,
    style: inputStyle(style, inputProps?.style),

    'data-qa': qa,
  };

  return (
    <>
      <FieldComponent ref={ref} {...componentProps} />
      {placeHolder}
    </>
  );
};

const BasicField: React.FC<InputInternalProps> = props => {
  const {value, style, inputProps, qa} = props;

  const [isOverflown, setIsOverflown] = useState<boolean>(false);
  const ref = useRef<HTMLInputElement>(null);

  const componentProps = {
    ...inputProps,

    style: inputStyle(style, inputProps?.style),

    'data-qa': qa,
  };

  const child = <FieldComponent ref={ref} {...componentProps} />;

  useEffect(() => {
    if (!style.isOverflowTooltip || !ref.current) {
      return;
    }

    setIsOverflown(ref.current.scrollWidth > ref.current.clientWidth);
  }, [value, style.isOverflowTooltip]);

  if (!style.isOverflowTooltip) {
    return child;
  }

  const noTooltip = style.isFocused || !isOverflown ? false : undefined;

  return (
    <Tooltip isPortal isOpen={noTooltip} content={value}>
      {child}
    </Tooltip>
  );
};

export const Field: React.FC<InputInternalProps> = props => {
  const Component = props.style.isMasked ? MaskedField : BasicField;

  return <Component {...props} />;
};
